/**
 * System health — a platform observability surface.
 *
 * Distinguishes Healthy / Degraded / Missing / Unconfirmed with explanations,
 * not just coloured dots. Model governance (which model, verified when,
 * against which source) is rendered so a non-engineer can understand it.
 */
import { useApi } from '../hooks/useApi';
import { getStatus } from '../api/client';
import type { Healthz } from '../api/types';
import { Card, ErrorBox, Loading, Tag } from '../components/Metric';

function Flag({ ok, yes, no }: { ok: boolean; yes: string; no: string }) {
  return <span className={`pill ${ok ? 'ok' : 'danger'}`}>{ok ? yes : no}</span>;
}

export function SystemPage() {
  const health = useApi<Healthz>(() =>
    getStatus<Healthz>('/healthz').then((r) => {
      if (!r.body) throw new Error('Health endpoint returned no body');
      return r.body;
    }),
  []);

  if (health.loading) return <Loading label="Checking…" />;
  if (health.error) return <ErrorBox message={health.error} />;
  if (!health.data) return null;

  const c = health.data.checks;

  return (
    <>
      <div className={`callout ${health.data.status === 'ok' ? 'info' : 'warn'}`}>
        <strong>Service status: {health.data.status.toUpperCase()}</strong>
        {health.data.status === 'ok'
          ? 'All dependencies healthy.'
          : 'One or more integrations are not configured. Inspection can run in development simulation mode, but this deployment is not production-ready. The health endpoint returns HTTP 503 in this state so an orchestrator will not route traffic here.'}
      </div>

      <div className="grid grid-2">
        <Card title="Dependencies">
          <table className="kv">
            <tbody>
              <tr>
                <td>Database</td>
                <td>
                  <Flag ok={c.database === 'ok'} yes="connected" no="unreachable" />
                </td>
              </tr>
              <tr>
                <td>Storage volume</td>
                <td>
                  <Flag ok={c.storage.writable} yes="writable" no="not writable" />
                  <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {c.storage.rootPath}
                  </div>
                </td>
              </tr>
              <tr>
                <td>Gemini API key</td>
                <td>
                  <Flag ok={c.inference.geminiKeyConfigured} yes="configured" no="MISSING" />
                </td>
              </tr>
              <tr>
                <td>Gatekeeper API key</td>
                <td>
                  <Flag ok={c.marketplace.gatekeeperApiKeyConfigured} yes="configured" no="MISSING" />
                </td>
              </tr>
              <tr>
                <td>Webhook secret</td>
                <td>
                  <Flag ok={c.marketplace.webhookSecretConfigured} yes="configured" no="MISSING" />
                </td>
              </tr>
              <tr>
                <td>Logto client</td>
                <td>
                  <Flag ok={c.marketplace.logtoClientIdConfigured} yes="configured" no="MISSING" />
                </td>
              </tr>
              <tr>
                <td>Balance unit</td>
                <td>
                  <span className={`pill ${c.marketplace.balanceUnit === 'unconfirmed' ? 'danger' : 'ok'}`}>
                    {c.marketplace.balanceUnit}
                  </span>
                  <div className="dim" style={{ marginTop: 4 }}>
                    Two marketplace docs disagree 100× on the unit; held until confirmed in writing.
                  </div>
                </td>
              </tr>
              <tr>
                <td>Live stream subscribers</td>
                <td className="mono">{c.liveSubscribers}</td>
              </tr>
            </tbody>
          </table>
        </Card>

        <Card
          title="Model bindings"
          note="Each binding records when the model identifier was verified against Google's documentation. If a binding is stale, re-verify before relying on it — model names change."
        >
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Task tier</th>
                  <th>Model</th>
                  <th>Verified</th>
                </tr>
              </thead>
              <tbody>
                {c.inference.modelBindings.map((b) => (
                  <tr key={b.tier}>
                    <td className="mono" style={{ fontSize: 11.5 }}>{b.tier}</td>
                    <td className="mono" style={{ fontSize: 11.5 }}>{b.modelId}</td>
                    <td style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                      {b.verifiedOn}{' '}
                      <a href={b.verifiedSource} style={{ fontSize: 10.5 }}>
                        source
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <div style={{ marginTop: 14 }}>
        <Card title="Architecture notes an operator of this system should know">
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--ink-2)' }}>
            <li style={{ marginBottom: 8 }}>
              <strong style={{ color: 'var(--ink)' }}>Inference is cloud, so output is advisory.</strong>{' '}
              Latency is in the hundreds of milliseconds to low seconds. That is
              fine for quality analytics and impossible for a deterministic
              safety circuit — which is the second, independent reason this
              platform never acts as an interlock.
            </li>
            <li style={{ marginBottom: 8 }}>
              <strong style={{ color: 'var(--ink)' }}>Media lives on a filesystem volume.</strong>{' '}
              Only relative keys are stored in Postgres, so{' '}
              <span className="mono">STORAGE_ROOT_PATH</span> can be moved
              without a data migration. There is no object-store dependency.
            </li>
            <li style={{ marginBottom: 8 }}>
              <strong style={{ color: 'var(--ink)' }}>Telemetry is EUROMAP 77 / 82.x / 84 only.</strong>{' '}
              EUROMAP 67 and 81 are refused at the API boundary because they are
              safety interfaces.
            </li>
            <li>
              <strong style={{ color: 'var(--ink)' }}>Availability and performance OEE are unavailable</strong>{' '}
              until EUROMAP 77 telemetry is connected. They are reported as null
              rather than computed with those factors assumed to be 100%.
            </li>
          </ul>
        </Card>
      </div>

      <div className="card-note" style={{ marginTop: 12 }}>
        {health.data.service} v{health.data.version} · checked at{' '}
        {new Date(health.data.time).toLocaleString()} · <Tag>rollout data, never guesses</Tag>
      </div>
    </>
  );
}
